<template>
  <div>
    <profile-upload-document
      :is-upload-document-open.sync="upload.isUploadDocumentOpen"
      :profile="upload.profile"
      :document-category="upload.documentCategory"
      :document-type="upload.documentType"
      @refresh="refetchStudents"
    />

    <ProfileListAddv2
      :create-profile-active.sync="createProfileActive"
      :selectedCourseSection="courseSectionData"
      :redirectOnComplete="false"
      @refetch-data="refetchStudents"
    />

    <div class="d-flex align-items-center justify-content-between mb-75">
      <div class="d-flex align-items-center">
        <h3 class="mb-0 mr-50">All Students</h3>
        <!-- fixme: disabled until proper implementation, see DEV-401 -->
        <div
          v-if="$can('roster.profile.create')"
          @click="addStudent"
          class="d-flex align-items-center text-muted cursor-pointer"
          style="margin-top: 3px; margin-left: 3px"
        >
          <feather-icon icon="PlusCircleIcon" size="18" class="mr-25" />
          <span>Add Student</span>
        </div>
      </div>

      <!-- <b-button variant="primary" class="ml-75" @click="isCurrent = !isCurrent">{{
        isCurrent ? 'Show Reassigned' : 'Show Current'
      }}</b-button> -->
    </div>

    <div v-if="courseSectionData.profiles.length || !isCurrent">
      <div class="mb-75">
        <b-row>
          <b-col cols="12" sm="8" md="7">
            <div class="d-flex align-items-center justify-content-start flex-wrap flex-sm-nowrap" style="gap: 0.675rem">
              <b-form-input v-model="searchQuery" class="drop-shadow-default" placeholder="Search..." style="max-width: 20rem" />
              <roster-active-filters v-model="columnFilters" :filters="columnFilterDefs" />
            </div>
          </b-col>

          <b-col cols="12" sm="4" md="5" class="d-flex align-items-center justify-content-end"></b-col>
        </b-row>
      </div>

      <b-card no-body class="mb-0 table-no-top table-text-nowrap">
        <b-table
          ref="refStudentsListTable"
          class="position-relative table-min-height"
          :items.sync="fetchProfiles"
          :fields="tableColumns"
          responsive
          primary-key="id"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          :sort-by-formatted="true"
          show-empty
          empty-text="No matching records found"
          @row-contextmenu="onRowContextMenu"
        >
          <!-- Column: multi select -->
          <!-- Every header: its label, plus a filter menu for filterable columns. -->
          <template #head()="data">
            <span>{{ data.label }}</span>
            <roster-column-filter-menu
              v-if="columnFilterDefs.some((filter) => filter.key === data.column)"
              :label="data.label"
              :options="columnFilterDefs.find((filter) => filter.key === data.column).options"
              :value="columnFilters[data.column] || []"
              @input="(selected) => (columnFilters = { ...columnFilters, [data.column]: selected })"
            />
          </template>

          <template #head(multiSelect)="data" tdClass="checkbox">
            <b-form-checkbox :checked="isAllChecked" @change="(val) => setAllChecked(val)" class="p-0"></b-form-checkbox>
          </template>

          <!-- Column: Session -->
          <template #cell(multiSelect)="data">
            <b-form-checkbox
              :checked="isProfileSelected(data.item.id)"
              @change="(val) => massSelectProfile(data.item.id, val)"
              class="p-0"
            ></b-form-checkbox>
          </template>

          <!-- Column: Session -->
          <template #cell(name)="data">
            <div class="d-flex align-items-center" style="gap: 0.625rem" :id="`student-${data.item.id}`">
              <div class="d-flex flex-column" style="gap: 0">
                <div
                  class="d-flex align-items-center font-weight-bold text-nowrap text-white mb-0"
                  style="font-size: 1.25rem; line-height: 1.75rem"
                >
                  <div class="cursor-pointer" @click="$router.push({ name: 'apps-students-view', params: { id: data.item.student.id } })">
                    {{ data.item.student.fullName }}
                  </div>
                  <div v-if="data.item.reassignFrom" class="ml-50">
                    <feather-icon
                      @click="redirectToReassigned(data.item.reassignFrom, false)"
                      icon="InfoIcon"
                      class="text-warning cursor-pointer"
                      :id="`sr-${data.item.id}-reassign-${data.item.reassignFrom.id}-tooltip`"
                      style="margin-bottom: 2px; z-index: 9"
                    />
                    <b-tooltip
                      placement="right"
                      @click="redirectToReassigned(data.item.reassignFrom, false)"
                      :title="`Reassigned From ${data.item.reassignFrom.name}`"
                      class="cursor-pointer"
                      :target="`sr-${data.item.id}-reassign-${data.item.reassignFrom.id}-tooltip`"
                    />
                  </div>
                  <div v-if="sectionField(data.item, 'replacedBy')" class="ml-50">
                    <feather-icon
                      @click="redirectToReplaced(sectionField(data.item, 'replacedBy'))"
                      icon="InfoIcon"
                      class="text-warning cursor-pointer"
                      :id="`sr-${data.item.id}-replaced-tooltip`"
                      style="margin-bottom: 2px; z-index: 9"
                    />
                    <b-tooltip
                      placement="right"
                      @click="redirectToReplaced(sectionField(data.item, 'replacedBy'))"
                      :title="`Replaced by another section`"
                      class="cursor-pointer"
                      :target="`sr-${data.item.id}-replaced-tooltip`"
                    />
                  </div>
                </div>
                <b-link
                  :to="{ name: 'apps-students-view', params: { id: data.item.student.id } }"
                  class="d-flex align-items-center text-muted"
                >
                  <small style="font-size: 1.05rem; line-height: 1.35rem">{{ data.item.student.myIpId }}</small>
                </b-link>
              </div>
            </div>

            <student-popover :target-id="data.item.id" :data="data.item" />
          </template>

          <!-- Column: Email -->
          <template #cell(email)="data">
            {{ data.item.student.email }}
          </template>

          <!-- Column: Session -->
          <template #cell(session)="data">
            <div
              v-if="data.value.type === 'pathway' && data.item.session.pathway"
              class="d-flex align-items-center text-nowrap"
              style="gap: 0.625rem"
            >
              <b-avatar
                size="36"
                :src="data.item.session.pathway.avatar"
                :text="avatarText(data.item.session.pathway.name)"
                :variant="`light-info`"
                :to="{ name: 'apps-roster-pathway-view', params: { id: data.item.session.pathway.id } }"
                style="margin-top: 2px"
                rounded
              />
              <div class="d-flex flex-column" style="gap: 0">
                <b-link
                  :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.value.id } }"
                  class="font-weight-bold text-nowrap text-white mb-0"
                >
                  {{ resolvePathwayCohortName(data.value) }}
                </b-link>
                <b-link
                  :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.value.id } }"
                  class="d-flex align-items-center text-muted text-nowrap"
                >
                  <lucide-icon name="Library" style="width: 14px; height: 14px; margin-right: 1px" />
                  <small> {{ data.item.session.pathway.name }}</small>
                </b-link>
              </div>
            </div>
            <div v-if="data.value.type === 'course'" class="d-flex align-items-center text-nowrap">
              <lucide-icon name="Book" style="width: 14px; height: 14px; margin-right: 5px" />
              Direct Student
            </div>
          </template>

          <!-- Column: Attributes - Program -->
          <template #cell(fundingSource)="data">
            <profile-field-select
              :profile="data.item"
              attribute="fundingSource"
              name="funding"
              :options="fundingSourceOptions"
              :resolve-variant="resolveProfileProgramVariant"
            />
          </template>

          <!-- Column: Attributes - Make-up -->
          <template #cell(makeup)="data">
            <action-badge-dropdown
              :value="(sectionField(data.item, 'makeup') || {}).enabled ? 'yes' : 'no'"
              :options="yesNoActualOptions"
              :resolve-variant="resolveProfileYNVariant"
              :disabled="!$can('roster.profile.update')"
              @select="(value) => assignMakeup(data.item.id, value === 'yes')"
            />
          </template>

          <!-- Column: Attributes - Retake -->
          <template #cell(retake)="data">
            <profile-section-field-select
              :profile="data.item"
              :section-id="courseSectionData.id"
              field="retake"
              name="retake"
              :options="yesNoActualOptions"
              :resolve-variant="resolveProfileYNVariant"
            />
          </template>

          <!-- Column: Compliance Score -->
          <template #cell(compliance)="data">
            <template
              v-if="!data.item.compliance || !data.item.compliance.sections || !data.item.compliance.sections[courseSectionData.id]"
            >
              <b-badge variant="danger">N/A</b-badge>
            </template>

            <template v-else>
              <b-badge
                :variant="`${resolveComplianceScoreVariant({
                  score: data.item.compliance.sections[courseSectionData.id].scores.overall,
                })}`"
                :id="`compliance-${data.item.id}`"
                class="text-capitalize"
                style="cursor: help"
              >
                {{
                  gradeScore({
                    score: data.item.compliance.sections[courseSectionData.id].scores.overall,
                    percent: true,
                    status: data.item.sections[courseSectionData.id].status,
                  })
                }}
                <font-awesome-icon v-if="data.item.compliance.sections[courseSectionData.id].locked" icon="fa-regular fa-lock" />
              </b-badge>

              <compliance-popover
                :target-id="data.item.id"
                :student-name="data.item.student.firstName"
                :compliance-data="data.item.compliance.sections[courseSectionData.id]"
                :profile-data="data.item"
              />
            </template>
          </template>

          <!-- Column: Status -->
          <template #cell(status)="data">
            <div v-if="!isCurrent">
              <b-badge
                variant="warning"
                class="text-capitalize cursor-pointer"
                :id="`course-cohort-student-row-${data.item.id}-reassigned-link`"
                @click="redirectToReassigned(data.item.reassignFrom)"
              >
                <feather-icon
                  icon="ExternalLinkIcon"
                  size="16"
                  v-if="data.item && data.item.session && $can(`roster.${data.item.session.type === 'pathway' ? 'cohort' : 'section'}.get`)"
                />
                Reassigned
              </b-badge>
              <b-tooltip
                v-if="data.item && data.item.session && $can(`roster.${data.item.session.type === 'pathway' ? 'cohort' : 'section'}.get`)"
                :title="`View Reassigned ${data.item.session.type === 'pathway' ? 'Cohort' : 'Section'}`"
                class="cursor-pointer"
                :target="`course-cohort-student-row-${data.item.id}-reassigned-link`"
              />
            </div>

            <b-dropdown v-else variant="primary" class="dropdown-edit-button" :popper-opts="{ positionFixed: true }">
              <template #button-content>
                <b-badge :variant="`${resolveProfileSectionStatusVariant(sectionField(data.item, 'status'))}`" class="text-capitalize">
                  {{ title(sectionField(data.item, 'status')) }}
                </b-badge>
              </template>
              <b-dropdown-item @click="updateSelectedProfilesStatus(null, data.item, 'enrolled')">Enrolled </b-dropdown-item>
              <b-dropdown-item @click="updateSelectedProfilesStatus(null, data.item, 'completed')">Completed </b-dropdown-item>
              <b-dropdown-item @click="updateSelectedProfilesStatus(null, data.item, 'graduated')">Graduated </b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item @click="updateSelectedProfilesStatus(null, data.item, 'not_graduated')">DNG </b-dropdown-item>
              <b-dropdown-item @click="updateSelectedProfilesStatus(null, data.item, 'withdrew')">Withdrew </b-dropdown-item>
              <b-dropdown-item @click="updateSelectedProfilesStatus(null, data.item, 'terminated')">Terminated </b-dropdown-item>
            </b-dropdown>
          </template>

          <!--
          <template #cell(status)="data">
            
          </template> -->
          <!-- Column: Modality -->
          <template #cell(modality)="data">
            <profile-field-select
              :profile="data.item"
              attribute="location"
              name="modality"
              :options="locationOptions"
              :resolve-variant="resolveProfileLocationStatus"
              :light="false"
            />
          </template>

          <!-- Column: Student Status -->
          <template #cell(studentStatus)="data">
            <b-badge :variant="`${resolveStudentStatusVariant(data.item.student.status)}`" class="text-capitalize">
              {{ data.item.student.status }}
            </b-badge>
          </template>

          <!-- Column: Transfer of Credit -->
          <template #cell(toc)="data">
            <action-badge-dropdown
              :value="(sectionField(data.item, 'toc') || {}).enabled ? 'yes' : 'no'"
              :options="yesNoActualOptions"
              :resolve-variant="resolveProfileYNVariant"
              :disabled="!$can('roster.cohort.student.credit')"
              @select="(value) => setCredit(data.item, value === 'yes')"
            />
          </template>

          <!-- Column: Course Exam Voucher -->
          <template #cell(examVoucher)="data">
            <profile-section-field-select
              :profile="data.item"
              :section-id="courseSectionData.id"
              field="examVoucher"
              name="exam voucher"
              :options="examVoucherOptions"
              :resolve-variant="resolveProfileYNVariant"
            />
          </template>

          <!-- Column: Course Exam Taken? -->
          <template #cell(examTaken)="data">
            <profile-section-field-select
              :profile="data.item"
              :section-id="courseSectionData.id"
              field="examTaken"
              name="exam taken"
              :options="yesNoOptions"
              :resolve-variant="resolveProfileYNVariant"
            />
          </template>

          <!-- Column: Course Exam Passed? -->
          <template #cell(examPassed)="data">
            <profile-section-field-select
              :profile="data.item"
              :section-id="courseSectionData.id"
              field="examPassed"
              name="exam passed"
              :options="yesNoOptions"
              :resolve-variant="resolveProfileYNVariant"
            />
          </template>

          <!-- Column: Course Exam Passed? -->
          <template #cell(labs)="data">
            <profile-section-field-select
              :profile="data.item"
              :section-id="courseSectionData.id"
              field="labCompleted"
              name="labs"
              :options="yesNoOptions"
              :resolve-variant="resolveProfileYNVariant"
            />
          </template>

          <!-- Column: Attributes - Payment Status -->
          <!-- <template #cell(paymentStatus)="data">
            <b-badge :variant="`${resolveProfilePaymentStatusVariant(data.item.attributes.paymentStatus)}`" class="text-capitalize">
              {{ title(data.item.attributes.paymentStatus) }}
            </b-badge>
          </template> -->

          <!-- Column: Attributes - Uploaded Resume -->
          <template #cell(uploadedResume)="data">
            <div class="d-flex align-items-center">
              <feather-icon
                :id="`profile-row-${data.item.id}-upload-resume-icon`"
                icon="UploadIcon"
                class="cursor-pointer mr-25"
                size="16"
                @click="startUpload(data.item, 'enrollment', 'resume')"
              />

              <b-tooltip title="Upload Resume" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-resume-icon`" />

              <profile-field-select
                :profile="data.item"
                attribute="uploadedResume"
                name="resume status"
                :options="uploadedResumeOptions"
                :resolve-variant="resolveProfileYNVariant"
              />
            </div>
          </template>

          <!-- Column: Attributes - Uploaded COE -->
          <template #cell(uploadedCoe)="data">
            <div class="d-flex align-items-center">
              <feather-icon
                :id="`profile-row-${data.item.id}-upload-coe-icon`"
                icon="UploadIcon"
                class="cursor-pointer mr-25"
                size="16"
                @click="startUpload(data.item, 'enrollment', 'coe')"
              />

              <b-tooltip title="Upload COE" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-coe-icon`" />

              <profile-field-select
                :profile="data.item"
                attribute="uploadedCoe"
                name="COE status"
                :options="uploadedCoeOptions"
                :resolve-variant="resolveProfileYNVariant"
              />
            </div>
          </template>

          <!-- Column: Attributes - Uploaded Transcript -->
          <template #cell(uploadedTranscript)="data">
            <div class="d-flex align-items-center">
              <feather-icon
                :id="`profile-row-${data.item.id}-upload-transcript-icon`"
                icon="UploadIcon"
                class="cursor-pointer mr-25"
                size="16"
                @click="startUpload(data.item, 'transcript', 'prior')"
              />

              <b-tooltip title="Upload Transcript" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-transcript-icon`" />

              <profile-field-select
                :profile="data.item"
                attribute="uploadedTranscript"
                name="transcript status"
                :options="uploadedTranscriptOptions"
                :resolve-variant="resolveProfileYNVariant"
              />
            </div>
          </template>

          <!-- Column: Attributes - VA Submission -->
          <template #cell(submittedToVa)="data">
            <profile-submitted-to-va-select :profile="data.item" />
          </template>

          <!-- Column: Total Students -->
          <template #cell(sessionDate)="data">
            <div class="text-nowrap">
              <span class="align-text-top text-capitalize">
                {{ moment(data.item.sessionDate).format('L') }}
              </span>
            </div>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown variant="primary" class="dropdown-edit-button" :popper-opts="{ positionFixed: true }">
              <template #button-content>
                <div class="p-50">
                  <feather-icon icon="MoreHorizontalIcon" size="21" />
                </div>
              </template>

              <b-dropdown-item v-if="$can('roster.profile.update')" @click="editRosterProfile(data.item)">
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span>Edit</span>
                  <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
                </div>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="data.item.sections[sectionId].makeup && data.item.sections[sectionId].makeup.enabled && $can('roster.profile.update')"
                @click="assignMakeup(data.item.id, false)"
              >
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span>Disable Make-Up</span>
                  <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
                </div>
              </b-dropdown-item>
              <b-dropdown-item v-else-if="$can('roster.profile.update')" @click="assignMakeup(data.item.id, true)">
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span>Enable Make-Up</span>
                  <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div class="mx-2 mb-1">
          <b-row>
            <b-col cols="12" md="3" class="d-flex align-items-center justify-content-center justify-content-sm-start">
              <span class="mb-0 text-muted cursor-not-allowed" id="student-list-statuses" style="cursor: help"
                >{{ dataMeta.of }} Total Entries</span
              >
              <student-list-statuses target-id="student-list-statuses" :data="studentListStatistics" />
              <span class="ml-3" v-if="courseSectionData.compliance.syncing"> Syncing... </span>
            </b-col>
            <!-- Pagination -->
            <b-col cols="12" md="9" class="d-flex align-items-center justify-content-center justify-content-sm-end mt-1 mt-sm-0">
              <div class="mr-2 vue-select-up">
                <span class="mb-0 mr-75">Rows Per Page</span>
                <v-select
                  v-model="perPage"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block"
                  :open="true"
                />
              </div>
              <span class="mr-2 ml-lg-50 mb-0">Page {{ currentPage }} of {{ totalPageCount }}</span>
              <b-pagination
                v-model="currentPage"
                :total-rows="totalProfiles"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-sm-0 ml-lg-50"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>

      <vue-context ref="courseSectionViewStudentListMenu" class="mw-8" v-slot="{ data }">
        <li v-if="data">
          <a
            v-if="$can('roster.profile.update')"
            class="d-flex align-items-center justify-content-between"
            @click="editRosterProfile(data.item)"
          >
            <span>Edit</span>
            <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
          </a>
          <!-- <a class="d-flex align-items-center justify-content-between">
            <span>Remove</span>
            <feather-icon icon="DeleteIcon" size="15" class="ml-50 text-muted" />
          </a> -->
        </li>

        <li v-if="data.item.sections[sectionId].makeup && data.item.sections[sectionId].makeup.enabled && $can('roster.profile.update')">
          <a
            v-if="$can('roster.profile.update')"
            class="d-flex align-items-center justify-content-between"
            @click="assignMakeup(data.item.id, false)"
          >
            <span>Disable Make-Up</span>
            <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
          </a>
        </li>

        <li v-else-if="data && $can('roster.profile.update')">
          <a
            v-if="$can('roster.profile.update')"
            class="d-flex align-items-center justify-content-between"
            @click="assignMakeup(data.item.id, true)"
          >
            <span>Enable Make-Up</span>
            <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
          </a>
        </li>
      </vue-context>
    </div>

    <div v-else>
      <b-card>
        <div class="d-flex align-items-center justify-content-center flex-column py-2">
          <feather-icon icon="AlertTriangleIcon" size="36" class="mb-1 text-warning" />
          <h4 class="mb-50">No students found</h4>
          <p class="text-center mb-0">No students found for this course section (all students)</p>
        </div>
      </b-card>
    </div>

    <b-modal
      id="modal-status-reason"
      :ok-title="this.editMode.isReasonMissing ? 'Please Enter Reason' : 'Submit'"
      centered
      size="lg"
      title="Provide a Reason for Status Update"
      ref="modal"
      @hidden="() => (editMode.isStatusReasonOpen = false)"
      :visible="editMode.isStatusReasonOpen"
      @ok="updateSelectedProfilesStatus"
    >
      <b-row>
        <b-col>
          <b-form-group v-if="!['enrolled', 'graduated'].includes(editMode.status)" label="Reason" label-for="status-reason">
            <b-form-input
              id="transfer-reason"
              v-model="editMode.statusReason"
              autofocus
              :state="editMode.statusReason && editMode.statusReason.length > 0"
              trim
            />
          </b-form-group>
          <b-form-group label="Propagate" label-for="propagation-mode">
            <v-select
              id="propagation-mode"
              v-model="editMode.propagation.mode"
              :options="propagationOptions"
              :reduce="(val) => val.value"
              :clearable="false"
              class="d-inline-block"
            />
          </b-form-group>
          <b-form-group v-if="'future' === editMode.propagation.mode" label="Effective As Of" label-for="propagation-effectiveAt">
            <b-form-datepicker id="propagation-effectiveAt" v-model="editMode.propagation.effectiveAt" />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>

    <student-add-credit
      v-if="isAddCreditOpen"
      :cohort-data="creditCohortData"
      :selected-profile="creditProfile"
      :is-add-credit-open.sync="isAddCreditOpen"
      :is-assign="true"
      :initial-section-id="courseSectionData.id"
      v-on:refresh="refetchStudents"
    ></student-add-credit>
  </div>
</template>

<script>
import ProfileSubmittedToVaSelect from '@/views/apps/roster/profile/components/ProfileSubmittedToVaSelect.vue';
import ProfileFieldSelect from '@/views/apps/roster/profile/components/ProfileFieldSelect.vue';
import ProfileSectionFieldSelect from '@/views/apps/roster/profile/components/ProfileSectionFieldSelect.vue';
import ActionBadgeDropdown from '@/views/apps/roster/profile/components/ActionBadgeDropdown.vue';
import RosterColumnFilterMenu from '@/views/apps/roster/profile/components/RosterColumnFilterMenu.vue';
import RosterActiveFilters from '@/views/apps/roster/profile/components/RosterActiveFilters.vue';
import StudentAddCredit from '@/views/apps/student/students-view/components/StudentAddCredit.vue';
import {
  sectionStatusOptions,
  fundingSourceOptions,
  locationOptions,
  examVoucherOptions,
  yesNoOptions,
  yesNoActualOptions,
  uploadedResumeOptions,
  uploadedCoeOptions,
  uploadedTranscriptOptions,
} from '@/views/apps/roster/profile/profileOptions';
import { submittedToVaOptions } from '@/views/apps/roster/profile/vaSubmission';
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownDivider,
  BDropdownItem,
  BFormCheckbox,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BLink,
  BModal,
  BPagination,
  BRow,
  BTable,
  BTooltip,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import { avatarText, gradeScore, title } from '@core/utils/filter';
import useCourseSectionStudentsList from './useCourseSectionStudentsList';
import moment from 'moment';
import VueContext from 'vue-context';
import { ref } from '@vue/composition-api';

// import ProfileUploadDocument from '@/views/apps/roster/courses/cohorts/view/components/ProfileUploadDocument.vue';
// import StudentTransferToSession from '@/views/apps/student/students-view/components/StudentTransferToSession.vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import useProfiles from '@/views/apps/roster/profile/useProfiles';
import useStudents from '@/views/apps/student/useStudents';
import usePathwayCohorts from '@/views/apps/roster/pathways/cohorts/usePathwayCohorts';
import ProfileListAddv2 from '../../../profile/create/ProfileListAddv2.vue';
import ProfileUploadDocument from '../../../pathways/cohorts/view/components/ProfileUploadDocument.vue';
import CompliancePopover from '@/views/apps/roster/courses/sections/view/components/CompliancePopover.vue';
import StudentPopover from '@/views/apps/roster/courses/sections/view/components/StudentPopover.vue';
import StudentListStatuses from '../../../pathways/cohorts/view/components/StudentListStatuses.vue';

export default {
  components: {
    ProfileSubmittedToVaSelect,
    ProfileFieldSelect,
    ProfileSectionFieldSelect,
    ActionBadgeDropdown,
    RosterColumnFilterMenu,
    RosterActiveFilters,
    StudentAddCredit,
    BFormDatepicker,
    // StudentTransferToSession,
    // ProfileUploadDocument,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BFormCheckbox,
    BButton,
    BTable,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BModal,
    BTooltip,
    ToastificationContent,
    vSelect,
    VueContext,
    ProfileListAddv2,
    ProfileUploadDocument,
    CompliancePopover,
    StudentPopover,
    StudentListStatuses,
  },
  data: () => {
    return {
      isAddCreditOpen: false,
      creditProfile: undefined,
      fundingSourceOptions,
      locationOptions,
      examVoucherOptions,
      yesNoOptions,
      yesNoActualOptions,
      uploadedResumeOptions,
      uploadedCoeOptions,
      uploadedTranscriptOptions,
      // On a section's list, status, retake, ToC, makeup, exams and labs filter by this section.
      columnFilterDefs: [
        { key: 'status', label: 'Section Status', options: sectionStatusOptions },
        { key: 'fundingSource', label: 'Funding', options: fundingSourceOptions },
        { key: 'modality', label: 'Modality', options: locationOptions },
        { key: 'makeup', label: 'Makeup', options: yesNoActualOptions },
        { key: 'retake', label: 'Retake', options: yesNoActualOptions },
        { key: 'toc', label: 'ToC', options: yesNoActualOptions },
        { key: 'examVoucher', label: 'Exam Voucher', options: examVoucherOptions },
        { key: 'examTaken', label: 'Exam Taken', options: yesNoOptions },
        { key: 'examPassed', label: 'Exam Passed', options: yesNoOptions },
        { key: 'labs', label: 'Labs', options: yesNoOptions },
        { key: 'uploadedResume', label: 'Resume', options: uploadedResumeOptions },
        { key: 'uploadedCoe', label: 'COE', options: uploadedCoeOptions },
        { key: 'uploadedTranscript', label: 'Transcript', options: uploadedTranscriptOptions },
        { key: 'submittedToVa', label: 'VA Submission', options: submittedToVaOptions },
      ],
      createProfileActive: false,

      upload: {
        isUploadDocumentOpen: false,

        profile: undefined,
        documentType: undefined,
        documentCategory: undefined,
      },

      tocEdit: {
        isModalOpen: false,
        isAssign: true,
        profile: undefined,
        course: undefined,
        note: '',
      },

      defaultEditMode: {
        enabled: true,
        selected: [],
        profile: null,
        status: null,
        statusReason: null,
        isStatusReasonOpen: false,
        isReasonMissing: false,
        propagation: { mode: 'none', effectiveAt: null },
      },
      editMode: {
        enabled: true,
        selected: [],
        profile: null,
        status: null,
        statusReason: null,
        isStatusReasonOpen: false,
        isReasonMissing: false,
        propagation: { mode: 'none', effectiveAt: null },
      },

      propagationOptions: [
        { label: 'None', value: 'none' },
        { label: 'Current', value: 'current' },
        { label: 'Current & Future', value: 'current_future' },
        { label: 'Future', value: 'future' },
        { label: 'All', value: 'all' },
      ],

      refreshComplianceTask: undefined,
    };
  },
  props: {
    sectionId: { type: String, required: true },
    courseSectionData: { type: Object, required: true },
  },
  watch: {
    'courseSectionData.compliance.syncing'() {
      if (this.courseSectionData.compliance.syncing) {
        this.refreshComplianceTask = setInterval(() => {
          this.refetchStudents();
          this.$emit('refresh-section');
        }, 5000);
      } else {
        clearInterval(this.refreshComplianceTask);
        this.refetchStudents();
      }
    },
    // Locking or unlocking the section changes every row's grade lock icon.
    'courseSectionData.compliance.locked'() {
      this.refetchStudents();
    },
  },
  computed: {
    /** The credit form picks from this list; on a section's page it is just this section. */
    creditCohortData() {
      const section = this.courseSectionData;
      const name = section.name || (section.course && section.course.name) || 'Section';
      return { sections: [{ id: section.id, name }] };
    },
    isAllChecked() {
      return this.idsOnPage.filter((id) => this.editMode.selected.indexOf(id) === -1).length === 0;
    },
  },
  methods: {
    /** Credit needs a note, so Yes opens the credit form; No removes it after confirming. */
    setCredit(profile, enabled) {
      if (enabled) {
        this.creditProfile = profile;
        this.$nextTick(() => (this.isAddCreditOpen = true));
        return;
      }

      this.$swal({
        title: 'Remove credit?',
        text: `Remove the transfer of credit for ${profile.student ? profile.student.fullName : 'this student'}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      }).then((result) => {
        if (!result.value) return;
        store
          .dispatch('rosterStoreModule/unassignCourseCredit', {
            id: profile.id,
            creditData: { enabled: false, sectionId: this.courseSectionData.id },
          })
          .then(() => this.refetchStudents())
          .catch((error) => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Failed to remove credit',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error.response && error.response.data ? error.response.data.message : error.message,
              },
            });
          });
      });
    },
    async assignMakeup(profileId, enabled) {
      console.log('enableMakeup', profileId);

      const result = await store
        .dispatch('rosterStoreModule/assignCourseMakeup', {
          profileId,
          enabled,
          sectionId: this.sectionId,
        })
        .then((response) => {
          this.refStudentsListTable.refresh();
        });

      console.log('assignMakeup', result);
    },
    redirectToReplaced(section) {
      return this.$router.push({ name: 'apps-roster-course-section-view', params: { id: section } });
    },
    redirectToReassigned(cohort) {
      if (cohort.type === 'course' && Array.isArray(cohort.sections) && cohort.sections.length > 0) {
        const id = cohort.sections[0].id ? cohort.sections[0].id : cohort.sections[0];
        return this.$router.push(`/roster/courses/sections/${id}`);
      }
      if (cohort.type === 'pathway') {
        const id = cohort.id ? cohort.id : cohort._id;
        return this.$router.push(`/roster/pathways/cohorts/${id}`);
      }
    },
    refetchStudents() {
      this.refetchData();
    },
    addStudent() {
      this.createProfileActive = true;
    },
    editRosterProfile(profile) {
      // console.log(profile);
      this.$router.push({
        name: 'apps-roster-profile-edit',
        params: { id: profile.id },
        query: { section: this.courseSectionData.id },
      });
    },
    onRowContextMenu(item, index, event) {
      this.$refs.courseSectionViewStudentListMenu.open(event, { item });
      event.preventDefault();
    },
    sectionField(profile, field) {
      const { id: sectionId } = this.courseSectionData;

      if (!profile.sections || !profile.sections[sectionId]) {
        return 'na';
      }

      return profile.sections[sectionId][field];
    },
    setAllChecked(val) {
      // if all of our current vals are selected and we're trying to update, noop
      if (val && this.isAllChecked) {
        return;
      }

      if (val) {
        // when checking, add all of the ids on the current visible page to our id buffer
        this.editMode.selected = this.editMode.selected.concat(this.idsOnPage).filter((id, index, items) => items.indexOf(id) === index);
      } else {
        // when checking, remove visible ids from the id list-pending
        this.editMode.selected = this.editMode.selected.filter((id) => this.idsOnPage.indexOf(id) === -1);
      }
    },
    isProfileSelected(profileId) {
      return this.editMode.selected.includes(profileId);
    },
    massSelectProfile(profileId, value) {
      if (value) {
        this.editMode.selected.push(profileId);
      } else {
        this.editMode.selected.splice(this.editMode.selected.indexOf(profileId), 1);
      }
    },
    updateSelectedProfilesStatus(event, _profile, _status) {
      if (event) {
        event.preventDefault();
      }

      const profileIds = this.editMode.selected.length === 0 ? [_profile.id] : this.editMode.selected;
      const status = _status ? _status : this.editMode.status;

      const pl = { status };

      const doUpdate = () => {
        store
          .dispatch('rosterStoreModule/updateProfileSections', {
            sectionId: this.sectionId,
            profileIds,
            sectionData: pl,
          })
          .then((response) => {
            this.refStudentsListTable.refresh();
            this.editMode.selected = [];
            this.editMode.status = null;
            this.editMode.statusReason = null;
            this.editMode.isStatusReasonOpen = false;
          });
      };

      this.$swal({
        title: `Update ${profileIds.length} Profiles`,
        text: `You're setting ${profileIds.length} profiles to ${title(status)}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Update',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          doUpdate();
        }
      });
    },
    startUpload(profile, documentCategory, documentType) {
      this.upload = {
        isUploadDocumentOpen: true,
        profile,
        documentCategory,
        documentType,
      };
    },
    async removeStudentsFromCourseSection(profileIds) {
      if (!profileIds || profileIds.length === 0) {
        console.log('no students selected or invalid id lsit');
        return;
      }

      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to remove this student from this session?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          const response = await this.$store
            .dispatch('rosterStoreModule/removeStudentsFromCourseSection', {
              sessionId: this.courseSectionData.id,
              profileIds,
            })
            .then(() => {
              this.refetchStudents();
            })
            .catch((error) => {
              console.error('failed to remove students to class', error);
            });
        }
      });
    },
  },
  setup(props) {
    const studentListStatistics = ref(props.courseSectionData.statistics);

    const {
      perPage,
      currentPage,
      totalPageCount,
      perPageOptions,
      sortBy,
      isSortDirDesc,
      refStudentsListTable,
      dataMeta,

      refetchData,
      tableColumns,
      fetchProfiles,
      searchQuery,
      columnFilters,
      totalProfiles,
      idsOnPage,
      isCurrent,
    } = useCourseSectionStudentsList(props.sectionId);

    const { resolvePathwayCohortName } = usePathwayCohorts();

    const {
      resolveProfileStatusVariant,
      resolveProfileSectionStatusVariant,
      resolveProfileLocationStatus,
      resolveProfileProgramVariant,
      resolveProfilePaymentStatusVariant,
      resolveProfileYNVariant,
      resolveComplianceScoreVariant,
    } = useProfiles();

    const { resolveStudentStatusVariant } = useStudents();

    return {
      // Sidebar

      fetchProfiles,
      refetchData,

      tableColumns,
      perPage,
      currentPage,
      totalPageCount,
      perPageOptions,
      sortBy,
      isSortDirDesc,
      refStudentsListTable,
      totalProfiles,
      idsOnPage,
      isCurrent,

      dataMeta,

      moment,

      searchQuery,
      columnFilters,

      resolveProfileStatusVariant,
      resolveProfileSectionStatusVariant,
      resolveProfileProgramVariant,
      resolveProfilePaymentStatusVariant,
      resolveProfileYNVariant,
      resolveProfileLocationStatus,
      resolveStudentStatusVariant,
      resolveComplianceScoreVariant,

      resolvePathwayCohortName,
      studentListStatistics,

      // Filter
      avatarText,
      title,
      gradeScore,
    };
  },
};
</script>
<style scoped>
.table-min-height {
  min-height: 300px;
}
</style>
